'use client'

import { queryOptions, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { createClient } from '@/lib/supabase/client'
import { useSession } from './useSession'
import type { AccessState } from '@sharpr/types'

// Module-scope for a stable identity — returned as `accessState` while loading and as
// the no-user query result. A per-render literal would thrash consumers that memo/effect
// on `accessState` (mirrors useUserPreferences' FALLBACK_PREFERENCES).
const NO_ACCESS_STATE: AccessState = {
  hasAccess: false,
  sportsAccess: [],
  accessRecords: [],
  isAdmin: false,
  trialUsed: false,
  billingHealth: 'ok',
  sportSeasons: [],
}

export const accessStateKey = ['access-state'] as const

// The access state's query, one symbol for every reader and invalidation (`accessStateQuery(userId).queryKey`; the
// whole entry, any user, is `accessStateKey`).
export const accessStateQuery = (userId: string | undefined) => queryOptions({
  queryKey: [...accessStateKey, userId] as const,
  queryFn: async (): Promise<AccessState> => {
    if (!userId) return NO_ACCESS_STATE
    const response = await fetch('/api/access/status')
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    return (await response.json()).data
  },
  staleTime: 5 * 60 * 1000,
  // Shaped by out-of-band events (Stripe webhooks, Customer Portal actions, admin changes): refetched on every mount so
  // the settings, admin and checkout pages read the latest server-side truth.
  refetchOnMount: 'always',
  retry: 1,
})

/**
 * The signed-in user's access state (AccessState, from /api/access/status): what they
 * hold — subscriptions, trials, comps, admin — with billing health and season dates.
 *
 * For the account record and what's shown about it (billing, trials, seasons, an admin
 * badge, analytics). Whether the viewer may see something (routing, gating, a sport's
 * tier) is useAccess's, which reads this once loaded.
 *
 * Returns:
 * - accessState: AccessState - full access state including sports access
 * - hasAccess: boolean - true if user has any active access
 * - loading: boolean - true while checking
 * - resolved: boolean - access is known: read at least once (kept through a failed refresh),
 *   or signed out. A check that never loaded is unresolved, not "no access".
 * - error: Error | null - error if status check fails
 * - refetch: () => void - manually refetch the access state
 */
export function useAccessState() {
  const { user, loading: userLoading } = useSession()
  const { data, isLoading, error, refetch } = useQuery({ ...accessStateQuery(user?.id), enabled: !userLoading && !!user })

  return {
    accessState: data ?? NO_ACCESS_STATE,
    hasAccess: data?.hasAccess ?? false,
    loading: userLoading || isLoading,
    resolved: !userLoading && (!user || data !== undefined),
    error: error as Error | null,
    refetch,
  }
}

const AWAIT_POLL_MS = 1_500

// A billing change the access state doesn't show yet: Stripe's webhook writes it after the action returns. While a change
// is awaited the state is refetched every AWAIT_POLL_MS until `settled` holds for it (the change is cleared and the access
// token re-minted, so its claim matches at the next cold load) or `timeoutMs` passes (cleared: the webhook never landed;
// the daily reconcile heals it). `pending` is the awaited change, for an optimistic display meanwhile.
export function useAwaitAccess<T>(settled: (state: AccessState, change: T) => boolean) {
  const { user } = useSession()
  const [wait, setWait] = useState<{ change: T; timeoutMs: number } | null>(null)
  const [settledCount, setSettledCount] = useState(0)
  const { data } = useQuery({ ...accessStateQuery(user?.id), enabled: !!user && wait !== null, refetchInterval: wait ? AWAIT_POLL_MS : false })

  // Settles while rendering the state that shows it (React's "adjusting state when a prop changes"), not in an effect.
  if (wait && data && settled(data, wait.change)) {
    setWait(null)
    setSettledCount(n => n + 1)
  }

  useEffect(() => {
    if (settledCount > 0) void createClient().auth.refreshSession().catch(() => {})
  }, [settledCount])

  useEffect(() => {
    if (!wait) return
    const giveUp = window.setTimeout(() => setWait(null), wait.timeoutMs)
    return () => window.clearTimeout(giveUp)
  }, [wait])

  const awaitChange = useCallback((change: T, timeoutMs = 30_000) => setWait({ change, timeoutMs }), [])
  return { pending: wait?.change ?? null, awaitChange }
}
